/**
 * Arabic catalog — the right-to-left (RTL) example locale.
 *
 * Selecting it drives `document.documentElement.dir = 'rtl'` (see the locale
 * store + RTL_LANGUAGES), so the whole shell mirrors. Declared
 * `: TranslationKeys`, so it is held to exact structural parity with the French
 * source at compile time. Strings outside the externalised tranche fall back to
 * French at runtime.
 */

import type { TranslationKeys } from './fr';

const ar: TranslationKeys = {
  common: {
    save: 'حفظ',
    cancel: 'إلغاء',
    delete: 'حذف',
    create: 'إنشاء',
    edit: 'تعديل',
    search: 'بحث',
    loading: 'جارٍ التحميل…',
    confirm: 'تأكيد',
    close: 'إغلاق',
    back: 'رجوع',
    next: 'التالي',
    previous: 'السابق',
    add: 'إضافة',
    remove: 'إزالة',
    apply: 'تطبيق',
    reset: 'إعادة تعيين',
    refresh: 'تحديث',
    retry: 'إعادة المحاولة',
    duplicate: 'تكرار',
    export: 'تصدير',
    import: 'استيراد',
    copy: 'نسخ',
    download: 'تنزيل',
    upload: 'رفع',
    filter: 'تصفية',
    all: 'الكل',
    none: 'لا شيء',
    yes: 'نعم',
    no: 'لا',
    ok: 'موافق',
    actions: 'إجراءات',
    more: 'المزيد',
    settings: 'الإعدادات',
    processing: 'جارٍ المعالجة…',
    readOnly: 'للقراءة فقط',
    empty: 'لا توجد عناصر',
  },

  nav: {
    brand: 'PubSocialNet',
    groups: {
      general: 'عام',
      creation: 'الإنشاء',
      prospection: 'التنقيب',
      ai: 'الذكاء الاصطناعي',
      system: 'النظام',
    },
    items: {
      dashboard: 'لوحة التحكم',
      content: 'المحتوى',
      calendar: 'التقويم',
      publishing: 'النشر',
      contentQueue: 'قائمة المحتوى',
      bulkSchedule: 'الجدولة الجماعية',
      platforms: 'المنصّات',
      ads: 'الإعلانات',
      mediaLibrary: 'مكتبة الوسائط',
      analytics: 'التحليلات',
      design: 'التصميم',
      workflows: 'سير العمل',
      crm: 'إدارة العملاء والعملاء المحتملين',
      discovery: 'الاستكشاف',
      prospectionRecommend: 'التوصيات',
      prospectionCalendar: 'تقويم التنقيب',
      accountIntelligence: 'ذكاء الحساب',
      carouselEditor: 'محرر العرض الدوار',
      richContentStudio: 'استوديو المحتوى الغني',
      prospect: 'قاعدة B2B',
      marketplace: 'السوق',
      social: 'عملاء اجتماعيون',
      outreach: 'التواصل عبر البريد',
      inbox: 'صندوق الوارد الاجتماعي',
      community: 'المجتمع',
      listening: 'الإنصات الاجتماعي',
      bio: 'رابط في النبذة',
      projects: 'المشاريع',
      aiTeam: 'فريق الذكاء الاصطناعي',
      aiOffice: 'مكتب الذكاء الاصطناعي',
      aiTeams: 'فرق الذكاء الاصطناعي',
      aiDeliverables: 'مخرجات الذكاء الاصطناعي',
      aiSchedules: 'جداول الذكاء الاصطناعي',
      aiControl: 'التحكم بالذكاء الاصطناعي',
      aiAuditTrail: 'سجل تدقيق الذكاء الاصطناعي',
      staffQuestions: 'أسئلة الوكلاء',
      grants: 'طلبات الصلاحيات',
      superadminFleet: 'أسطول المنصة',
      orchestration: 'تنسيق الذكاء الاصطناعي',
      aiAssistant: 'مساعد الذكاء الاصطناعي',
      contentStudio: 'استوديو المحتوى',
      ghostwriter: 'كاتب الظل بالذكاء الاصطناعي',
      repurpose: 'إعادة الاستخدام',
      hookLibrary: 'مكتبة الجُمَل الافتتاحية',
      brandVoice: 'صوت العلامة التجارية',
      abCopy: 'اختبارات A/B للنصوص',
      suggestions: 'اقتراحات الذكاء الاصطناعي',
      personalization: 'التخصيص',
      communityAi: 'مجتمع الذكاء الاصطناعي',
      ragDocuments: 'مستندات RAG',
      memory: 'ذاكرة الذكاء الاصطناعي',
      approvals: 'الموافقات',
      aiActivity: 'نشاط الذكاء الاصطناعي',
      settings: 'الإعدادات',
    },
    header: {
      openMenu: 'فتح القائمة',
      closeMenu: 'إغلاق القائمة',
      toggleSidebar: 'طيّ الشريط الجانبي أو فرده',
      breadcrumbRoot: 'PubSocialNet',
      breadcrumbAria: 'مسار التنقّل',
      searchPlaceholder: 'بحث…',
      openCommandPalette: 'فتح لوحة الأوامر',
      quickActions: 'إجراءات سريعة',
      create: 'إنشاء',
      createItem: 'إنشاء عنصر',
      shortcuts: 'اختصارات لوحة المفاتيح',
      showShortcuts: 'عرض اختصارات لوحة المفاتيح',
    },
    quickCreate: {
      newContent: 'محتوى جديد',
      newContact: 'جهة اتصال جديدة',
      newCompany: 'شركة جديدة',
      newCampaign: 'حملة جديدة',
      newProject: 'مشروع جديد',
      newDesign: 'تصميم جديد',
    },
    quickAccess: {
      search: 'بحث (⌘K)',
      newContent: 'محتوى جديد',
      newContact: 'جهة اتصال جديدة',
      newDeal: 'صفقة جديدة',
      inbox: 'صندوق الوارد الاجتماعي',
      assistant: 'مساعد الذكاء الاصطناعي',
    },
    user: {
      fallbackName: 'مستخدم',
      connectedAccount: 'حساب متصل',
      settings: 'الإعدادات',
      signout: 'تسجيل الخروج',
    },
    footer: {
      environment: 'بيئة التشغيل',
      whatsNew: 'ما الجديد',
      shortcuts: 'الاختصارات',
      privacy: 'الخصوصية',
      links: 'روابط التذييل',
      statusTitle: 'حالة الخدمة (يتم فحص ‎/health كل ٣٠ ثانية)',
      operational: 'يعمل',
      unavailable: 'الخدمة غير متاحة',
      checking: 'جارٍ التحقق…',
    },
    primaryNav: 'التنقّل الرئيسي',
  },

  settings: {
    title: 'الإعدادات',
    subtitle:
      'أرصدة الذكاء الاصطناعي والفوترة، وسجل الوكلاء والنماذج، والفريق، والملف الشخصي، والاتصالات الاجتماعية.',
    navAria: 'تنقّل الإعدادات',
    tabs: {
      credits: 'الأرصدة والفوترة',
      subscription: 'الاشتراك',
      referral: 'الإحالة',
      aiRegistry: 'سجل الذكاء الاصطناعي',
      models: 'نماذج الذكاء الاصطناعي',
      governance: 'حوكمة الذكاء الاصطناعي',
      trustCenter: 'مركز الثقة',
      organization: 'المؤسسة',
      notifications: 'الإشعارات',
      roles: 'الأدوار والأذونات',
      connectors: 'الموصّلات',
      developers: 'واجهة المطوّرين',
      integrations: 'التكاملات',
      connections: 'الحسابات المتصلة',
      team: 'الفريق',
      profile: 'الملف الشخصي',
    },
  },

  profile: {
    tabsAria: 'أقسام الملف الشخصي',
    tabProfile: 'الملف الشخصي',
    tabConnections: 'الاتصالات',
    emailVerified: 'تم التحقق من البريد الإلكتروني',
    emailUnverified: 'لم يتم التحقق من البريد الإلكتروني',
    noRole: 'لا يوجد دور',
    stats: {
      roles: 'الأدوار',
      session: 'الجلسة',
      expiresAt: 'تنتهي في',
    },
    account: {
      title: 'الحساب',
      name: 'الاسم',
      email: 'البريد الإلكتروني',
      username: 'المعرّف',
      tenant: 'المستأجر (tenant)',
      userId: 'معرّف المستخدم',
      roles: 'الأدوار',
    },
    prefs: {
      title: 'التفضيلات',
      language: 'اللغة',
      languageHint: 'يتم تذكّر التفضيل على هذا الجهاز.',
      theme: 'السمة',
      themeHint: '«النظام» يتبع إعداد الفاتح/الداكن في المتصفح.',
      themeSystem: 'النظام',
      themeLight: 'فاتح',
      themeDark: 'داكن',
      appearance: 'المظهر',
      appearanceHint: 'تبديل الفاتح/الداكن ولون التمييز — متاح أيضًا من الرأس.',
    },
  },

  platforms: {
    connections: {
      needsReconnectBadge: 'إعادة الربط مطلوبة',
      needsReconnectHint:
        'تعذّر على الخادم قراءة الرمز المخزَّن — لم يعد بإمكان هذا الربط النشر. أعد ربط الحساب لإصلاح ذلك.',
      reconnectAction: 'إعادة الربط',
    },
  },

  publishingSource: {
    label: 'مصدر المحتوى',
    aiGenerated: 'ذكاء اصطناعي (مساعد بالخطوات)',
    aiGeneratedHint: 'افتح مساعد الذكاء الاصطناعي لإنشاء المحتوى خطوة بخطوة، ثم أرسله إلى هنا.',
    aiOpenWizard: 'افتح مساعد الذكاء الاصطناعي',
    manualExternal: 'يدوي — وسائط خارجية',
    manualExternalHint: 'مكتوب يدويًا مع وسائط من مصدر خارجي (رفع، مكتبة، صورة بالذكاء الاصطناعي أو استيراد رابط).',
    manualDesign: 'يدوي — تصميم جرافيكي',
    manualDesignHint: 'مكتوب يدويًا مع تصميم مرئي منشأ في مصمّم الجرافيك.',
    designAttached: 'تم إرفاق تصميم مرئي بالمنشور.',
  },

  publishingWizard: {
    pageTitle: 'مساعد الذكاء الاصطناعي — إنشاء منشور',
    pageSubtitle:
      'أنشئ منشورًا خطوة بخطوة (الموضوع ← العنوان ← الوصف ← النص المفصّل)، مع مراجعة بالذكاء الاصطناعي في كل مرحلة، ثم أرسله إلى المحرّر.',
    backToComposer: 'العودة إلى المحرّر',
    stages: {
      theme: {
        label: 'الموضوع',
        fieldLabel: 'موضوع المحتوى',
        placeholder: 'مثال: إطلاق منتج، نصائح موارد بشرية، دراسة حالة…',
        hint: 'الموضوع يقود كل المراحل التالية.',
      },
      title: {
        label: 'العنوان',
        fieldLabel: 'العنوان / الجملة الجاذبة',
        placeholder: 'عنوان جذّاب…',
        hint: 'اختر مقترحًا أو عدّل عنوان المنشور.',
      },
      description: {
        label: 'الوصف',
        fieldLabel: 'الوصف / الملخّص',
        placeholder: 'ملخّص من جملة أو جملتين…',
        hint: 'ملخّص قصير يُوسَّع في المرحلة التالية.',
      },
      detail: {
        label: 'النص المفصّل',
        fieldLabel: 'نص المنشور الكامل',
        placeholder: 'النص الكامل، جاهز للنشر…',
        hint: 'النص النهائي المُرسَل إلى المحرّر.',
      },
    },
    seedLabel: 'كلمات مفتاحية / سياق (اختياري)',
    seedHint: 'يساعد في توليد الموضوع — القطاع، الجمهور، النبرة المطلوبة…',
    seedPlaceholder: 'مثال: القطاع، الجمهور، النبرة المطلوبة…',
    platformLabel: 'المنصّة (سياق)',
    generate: 'توليد المقترحات',
    regenerate: 'إعادة التوليد',
    generating: 'جارٍ التوليد…',
    candidatesTitle: 'المقترحات',
    noCandidatesError: 'لم تُعَد أي مقترحات — اكتب نصّك مباشرةً أدناه.',
    genericError: 'فشل التوليد — أعد المحاولة أو اكتب نصّك يدويًا.',
    modelLabel: 'النموذج',
    reviewSectionTitle: '🧠 مراجعة الذكاء الاصطناعي لهذه المرحلة',
    back: 'المرحلة السابقة',
    next: 'المرحلة التالية',
    sendToComposer: 'إرسال إلى المحرّر',
    sentToComposer: 'تم إرسال المنشور إلى المحرّر.',
    emptyStageError: 'املأ النص أو ولّده قبل المتابعة.',
    quickCreateCta: 'التوليد بالذكاء الاصطناعي',
  },

  publishingTracking: {
    pageTitle: 'تتبّع المنشورات',
    pageSubtitle: 'سجلّ المقاييس والاتجاه والإجراءات بعد النشر لكل منشور منشور.',
    pickerLabel: 'المنشورات المنشورة',
    pickerEmpty: 'لا يوجد منشور منشور بعد.',
    historyTitle: 'سجلّ المقاييس',
    historyEmpty: 'لا توجد لقطة مقاييس مسجّلة لهذا المنشور.',
    timeseriesTitle: 'الاتجاه العام (آخر 30 يومًا)',
    timeseriesEmpty: 'بيانات غير كافية لرسم اتجاه.',
    trackingUnavailable: 'التتبّع غير متاح بعد في هذه البيئة.',
    refresh: 'تحديث المقاييس',
    refreshing: 'جارٍ التحديث…',
    refreshed: 'تم تحديث المقاييس.',
    actionsTitle: 'الإجراءات',
    actionBoost: 'ترويج',
    actionReshare: 'إعادة نشر',
    actionReply: 'رد',
    actionRetry: 'إعادة المحاولة',
    actionReschedule: 'إعادة الجدولة',
    replyPlaceholder: 'ردّك…',
    replySend: 'إرسال الرد',
    rescheduleLabel: 'التاريخ والوقت الجديد',
    rescheduleConfirm: 'إعادة الجدولة',
    actionUnsupported: 'الإجراء غير مدعوم لهذه المنصّة أو الحساب.',
    actionSuccess: 'اكتمل الإجراء بنجاح.',
    actionError: 'فشل الإجراء — يُرجى إعادة المحاولة.',
    openTracking: 'التتبّع والإجراءات',
    engagementTitle: 'التفاعل',
    likes: 'إعجابات',
    comments: 'تعليقات',
    shares: 'مشاركات',
    reach: 'الوصول',
    impressions: 'الظهور',
    capturedAt: 'التُقطت في',
    noJobContext: 'تفاصيل المهمة غير معروفة (وصول مباشر) — بعض الإجراءات مخفية.',
  },
};

export default ar;
